<template>
	<div>
		<div v-if="blackout && open" class="slideup-blackout" :position="position" />
		<transition name="slideup-transition" appear>
			<div
				v-if="open"
				class="flow-slideup-container"
				:position="position"
				:state="state"
				:border="border"
			>
				<div v-if="$slots['header']" class="flow-slideup-header">
					<!-- @slot Used to display header or icons -->
					<slot name="header" />
				</div>
				<div class="flow-slideup-body">
					<!-- @slot Used to display content of slideup such as messages or user input -->
					<slot />
				</div>
				<div v-if="$slots['footer']" class="flow-slideup-footer">
					<!-- @slot Used to display actions that is buttons or links -->
					<slot name="footer" />
				</div>
			</div>
		</transition>
	</div>
</template>
<script>
import { defineComponent } from "vue";
/**
 * Slide up is an overlay element that literally slides up from the bottom in the upward direction. It primarily behaves as a dialog box. Slide-up displays important information that user needs to acknowledge. It appears over the interface inside the content wrapper and block further interactions.
 */
export default defineComponent({
	name: "Slideup",
	props: {
		/**
		 * true | false
		 * <br/> Used to enable/disable blackout
		 *
		 */
		blackout: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		open: {
			type: Boolean,
			default: true
		},
		/**
		 * 'default' | 'error' | 'warning'
		 */
		state: {
			type: String,
			default: "default",
			validator(value) {
				return ["warning", "error", "default"].includes(value);
			}
		},
		/**
		 * 'fixed' | 'absolute'
		 */
		position: {
			type: String,
			default: "absolute"
		},
		/**
		 * 'top' | 'bottom' | 'left' | 'right' | 'none'
		 */
		border: {
			type: String,
			default: "top"
		}
	}
});
</script>
<style lang="scss">
$states: (
	"error": var(--slideout-danger-gradient),
	"warning": var(--slideout-warning-gradient)
);
$borders: ("top", "bottom", "left", "right", "none");

div.flow-slideup-container {
	// slideup base css
	display: flex;
	flex-direction: column;
	padding: 0px 16px 0px 16px;
	width: 100%;
	// max-width: 400px;
	background-color: var(--slideout-background) !important;
	z-index: 102;
	min-height: 200px;
	height: auto;

	&[position="fixed"] {
		position: fixed;

		max-width: 400px;
	}
	&[position="absolute"] {
		position: absolute;
	}

	right: 0px;
	bottom: 0px;

	// iterating over slideup states
	@each $state, $color in $states {
		&[state="#{$state}"] {
			background: $color;
		}
	}

	// iterating over slideup borders
	&[border]:not([border="none"]) {
		@each $border in $borders {
			&[border="#{$border}"] {
				border-#{$border}: 1px solid var(--slideout-border);
			}
		}
	}

	// slideup header css
	> div.flow-slideup-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 0px 16px 0px;

		> *:last-child:not(:only-child) {
			margin-left: auto;
		}
	}

	// slideup footer css
	> div.flow-slideup-footer {
		margin-top: auto;
		padding: 16px 0px 16px 0px;
		display: flex;
		align-items: center;

		> * + * {
			margin-left: 16px;
		}

		> button[state="icon"] {
			flex-shrink: 0;
		}
	}
}

// slideup blackout
.slideup-blackout {
	content: "";
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--slideout-blackout);
	transition: background 0.3s linear;
	z-index: 101;
}
/***************** slideup transition related classes *********************/
.slideup-transition-enter,
.slideup-transition-leave-to {
	transform: translateY(200%);
}
.slideup-transition-enter-to,
.slideup-transition-leave {
	transform: translateY(0%);
}

.slideup-transition-enter-active {
	transition: transform 0.4s ease-out;
}

.slideup-transition-leave-active {
	transition: transform 0.4s ease-in;
}
/**************************************/
</style>
